import {
  ActionBar,
  Button,
  ClassesIllustration,
  Dropdown,
  DropdownMenu,
  EmptyState,
  ImportIcon,
  Select,
  type SelectOptionType,
} from '@bp/ui-components';
import { graphql } from 'client/gql';
import { BpCard } from 'components/BpCard/BpCard';
import { ClassGroupProfilesModal } from 'components/ClassGroups/modals/ClassGroupProfilesModal';
import { DivisionsModal } from 'components/Divisions/modals/DivisionsModal';
import { XlsxStudentImportModal } from 'components/XlsxImport/modals/XlsxStudentImportModal';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { useConfirm } from 'hooks/useConfirm';
import type { BpClassType } from 'pages/Institution/subpages/InstitutionClassesSubpage';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SingleValue } from 'react-select';
import { useMutation, useQuery } from 'urql';
import { getColorName } from 'utils/colorHelper';
import { showErrorToast } from 'utils/showErrorToast';
import { showSuccessToast } from 'utils/showSuccessToast';
import { naturalStringSort } from 'utils/stringHelper';
import { OrganizationConfigContext } from '../../context/OrganizationConfigContext';
import { ClassesListItem } from './ClassesListItem';
import { ClassesListModal } from './modals/ClassesListModal';
import { ClassProfilesModal } from './modals/ClassProfilesModal';

const DeleteClassMutationDocument = graphql(`
  mutation DeleteClass($where: ClassWhere!, $delete: ClassDeleteInput!) {
    deleteClasses(where: $where, delete: $delete) {
      nodesDeleted
    }
  }
`);

const ClassesListPageQueryDocument = graphql(`
  query ClassesListPageData($organizationUuid: ID!, $schoolyearUuid: ID!) {
    classes(
      where: { organization: { uuid: $organizationUuid }, schoolYear: { uuid: $schoolyearUuid } }
      options: { sort: [{ name: ASC }] }
    ) {
      __typename
      uuid
      foreignRefId
      updatedAt
      createdAt
      name
      color
      grade
      gradeGroup
      shortName
      managedBy
      schoolYear {
        __typename
        uuid
      }
      divisionsConnection {
        __typename
        edges {
          __typename
          node {
            __typename
            uuid
          }
        }
      }
      tutorsConnection(sort: { edge: { order: ASC } }) {
        __typename
        edges {
          __typename
          properties {
            __typename
            order
          }
          node {
            __typename
            uuid
            selectName
          }
        }
      }
      membersConnection {
        __typename
        edges {
          __typename
          node {
            __typename
            uuid
            selectName
            foreignRefId
          }
        }
      }
    }
    ...ClassesListItemQueryFields
  }
`);

export const ClassesList = () => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();

  const { ConfirmationDialog, confirm } = useConfirm({
    defaultTitle: t('delete.headline'),
    defaultMessage: t('delete.message', {
      type: t('classes.titleSingular'),
      context: 'female',
    }),
    defaultConfirmText: t('delete.delete'),
  });

  const { schoolyears, currentSchoolYear } = useContext(OrganizationConfigContext);
  const [selectedYearUuid, setSelectedYearUuid] = useState<string>(currentSchoolYear?.uuid || '');
  const [selectedClass, setSelectedClass] = useState<BpClassType | null>(null);
  const [classModalOpen, setClassModalOpen] = useState<boolean>(false);
  const [classProfilesModalOpen, setClassProfilesModalOpen] = useState<boolean>(false);
  const [divisionsModalOpen, setDivisionsModalOpen] = useState<boolean>(false);
  const [groupProfilesModalOpen, setGroupProfilesModalOpen] = useState<boolean>(false);
  const [importStudentModalOpen, setImportStudentModalOpen] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');

  const [{ data }, refetch] = useQuery({
    query: ClassesListPageQueryDocument,
    variables: {
      organizationUuid,
      schoolyearUuid: selectedYearUuid,
    },
  });

  const classesData = data?.classes;

  const schoolYearOptions: SelectOptionType[] =
    schoolyears?.map(({ name, uuid }) => {
      return {
        label: name,
        value: uuid,
      };
    }) ?? [];

  const [, deleteClass] = useMutation(DeleteClassMutationDocument);

  const classes = useMemo((): BpClassType[] => {
    return (
      classesData
        ?.sort((a, b) => naturalStringSort(a.name, b.name))
        .map((c) => {
          return {
            uuid: c.uuid,
            name: c.name,
            shortName: c.shortName,
            grade: c.grade ? Number(c.grade) : 0,
            gradeGroup: c.gradeGroup,
            tutor1Uuid: c?.tutorsConnection?.edges[0] ? c.tutorsConnection?.edges[0].node.uuid : '',
            tutor2Uuid: c?.tutorsConnection?.edges[1] ? c.tutorsConnection?.edges[1].node.uuid : '',
            tutor3Uuid: c?.tutorsConnection?.edges[2] ? c.tutorsConnection?.edges[2].node.uuid : '',
            color: {
              color: c.color ?? '',
              colorLabel: getColorName(c.color ?? ''),
            },
            source: c.managedBy,
            divisionUuids: c.divisionsConnection.edges.map((d) => d.node.uuid),
            profileUuids: c.membersConnection.edges.map((p) => p.node.uuid) ?? [],
            schoolYearUuid: c.schoolYear.uuid,
          };
        }) ?? []
    );
  }, [classesData]);

  const filteredClasses = useMemo(() => {
    return classes?.filter((c) => c.name.toLowerCase().includes(search.toLowerCase())) ?? [];
  }, [classes, search]);

  const colorsInUse = classes.map((c) => c.color ?? null);

  function handleEditClass(uuid: string) {
    setSelectedClass(classes?.find((c) => c.uuid === uuid) ?? null);
    setClassModalOpen(true);
  }

  function handleAssignClassProfiles(uuid: string) {
    setSelectedClass(classes?.find((c) => c.uuid === uuid) ?? null);
    setClassProfilesModalOpen(true);
  }

  function handleEditDivisions(uuid: string) {
    setSelectedClass(classes?.find((c) => c.uuid === uuid) ?? null);
    setDivisionsModalOpen(true);
  }

  function handleAssignGroupProfiles(uuid: string) {
    setSelectedClass(classes?.find((c) => c.uuid === uuid) ?? null);
    setGroupProfilesModalOpen(true);
  }

  async function handleDelete(uuid: string) {
    const res = await confirm();
    if (res) {
      const response = await deleteClass({
        where: { uuid: uuid },
        delete: {
          foreignSyncMeta: [
            {
              where: {
                node: {
                  syncTarget: {
                    Class: {
                      uuid: uuid,
                    },
                  },
                },
              },
            },
          ],
        },
      });
      if (response.error) {
        showErrorToast(response.error);
      } else {
        showSuccessToast(t('classes.classDeleted'));
        refetch();
      }
    }
  }

  return (
    <>
      <ActionBar
        onGlobalFilterChange={(value) => setSearch(value)}
        showAddButton
        onAddClick={() => setClassModalOpen(true)}
        addButtonText={t('classes.add')}
        extendedActionsRight={
          <>
            <div style={{ width: '136px' }}>
              <Select
                value={schoolYearOptions.find((y) => y.value === selectedYearUuid)}
                options={schoolYearOptions}
                onChange={(option) => {
                  const value = (option as SingleValue<SelectOptionType>)?.value as string | undefined;
                  if (value) {
                    setSelectedYearUuid(value);
                  }
                }}
                name='school-year-select'
                dense
              />
            </div>
            <Dropdown
              trigger={
                <Button icon={<ImportIcon />} className='ml-2' hierarchy='tertiary'>
                  {t('classes.import')}
                </Button>
              }
            >
              <DropdownMenu
                data={[
                  {
                    label: t('import.importType', { type: t('classes.title', { count: 2 }) }),
                  },
                  {
                    label: t('import.importType', { type: t('students.title', { count: 2 }) }),
                    onClick: () => setImportStudentModalOpen(true),
                  },
                ]}
              />
            </Dropdown>
          </>
        }
      />

      {filteredClasses.length === 0 ? (
        <BpCard noPadding preventCollapsible>
          <EmptyState
            icon={<ClassesIllustration />}
            title={t('classes.empty.title')}
            subtitle={t('classes.empty.subtitle')}
            forcedHeight='30vh'
          />
        </BpCard>
      ) : (
        filteredClasses.map((c) => (
          <ClassesListItem
            key={c.uuid}
            bpClass={c}
            onEditClass={handleEditClass}
            onAssignClassProfiles={handleAssignClassProfiles}
            onEditDivisions={handleEditDivisions}
            onAssignGroupProfiles={handleAssignGroupProfiles}
            onDelete={handleDelete}
            query={data}
          />
        ))
      )}

      <ClassesListModal
        selectedClass={selectedClass ?? undefined}
        schoolYearUuid={selectedYearUuid}
        colorsInUse={colorsInUse}
        isOpen={classModalOpen}
        onClose={() => {
          setClassModalOpen(false);
          setSelectedClass(null);
          refetch();
        }}
      />

      {selectedClass && (
        <ClassProfilesModal
          isOpen={classProfilesModalOpen}
          bpClass={selectedClass}
          schoolYearUuid={selectedYearUuid}
          onClose={() => {
            setClassProfilesModalOpen(false);
            setSelectedClass(null);
            refetch();
          }}
        />
      )}

      {selectedClass && (
        <DivisionsModal
          bpClass={selectedClass}
          isOpen={divisionsModalOpen}
          onClose={() => {
            setDivisionsModalOpen(false);
            setSelectedClass(null);
            refetch();
          }}
        />
      )}

      {selectedClass && (
        <ClassGroupProfilesModal
          bpClass={selectedClass}
          isOpen={groupProfilesModalOpen}
          onClose={() => {
            setGroupProfilesModalOpen(false);
            setSelectedClass(null);
            refetch();
          }}
        />
      )}

      <XlsxStudentImportModal
        isOpen={importStudentModalOpen}
        classes={classes}
        onClose={() => setImportStudentModalOpen(false)}
        query={data}
      />

      <ConfirmationDialog />
    </>
  );
};
